import { useEffect, useCallback } from 'react'
import Tooltip from '@mui/material/Tooltip'
import { useAuth } from '../../contexts/AuthContext'
import { dealPageId } from '../../utils/dealPageId'
import { deriveSubdomainSlug } from '../../utils/subdomainFallback'
import { slugify } from '../../utils/slugify'
import './MainNav.css'

// Inline SVG icons matching the design's stroke style
const GridIcon = (p: any) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
    <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
  </svg>
)
const PlayIcon = (p: any) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M8 5v14l11-7Z"/></svg>
)
const SettingsIcon = (p: any) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.9 2.9l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.9-2.9l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.9-2.9l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.9 2.9l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z"/>
  </svg>
)
const EditIcon = (p: any) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
  </svg>
)
const EyeIcon = (p: any) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12Z"/><circle cx="12" cy="12" r="3"/>
  </svg>
)
const LogoutIcon = (p: any) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>
  </svg>
)
const ChevDownIcon = (p: any) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="m6 9 6 6 6-6"/>
  </svg>
)

interface MainNavProps {
  onOpenEditor: () => void
  onCloseEditor?: () => void
  onOpenSettings?: () => void
}

const accent = '#3b82f6'

export default function MainNav({ onOpenEditor, onCloseEditor, onOpenSettings }: MainNavProps) {
  const { user, logout, currentSubdomain } = useAuth()

  const handlePublish = useCallback(async () => {
    try {
      const editor = (window as any).editor
      if (!editor) { alert('Editor not loaded.'); return }

      // Export every page in the Page Manager, not just whichever one is
      // currently selected in the canvas — otherwise publishing a multi-page
      // template silently drops every page but the active one.
      const allPages = editor.Pages.getAll() || []
      const mainPage = editor.Pages.getMain ? editor.Pages.getMain() : allPages[0]
      const activeDealId = (user as any)?.activeCrmDeal?.dealId
      const activeDealPageId = activeDealId ? dealPageId(activeDealId) : null
      const pages = allPages.map((page: any) => {
        const mainComponent = page.getMainComponent ? page.getMainComponent() : undefined
        const isMain = mainPage && page.getId() === mainPage.getId()
        return {
          slug: isMain ? '' : slugify(page.getName?.() || page.getId()),
          name: page.getName?.() || 'Untitled',
          html: (mainComponent ? editor.getHtml({ component: mainComponent }) : editor.getHtml()) || '',
          css: (mainComponent ? editor.getCss({ component: mainComponent }) : editor.getCss()) || '',
          // getJs() takes the same `component` scoping option as getHtml/getCss
          // (it just defaults to exporting JS for "all components" when
          // omitted) -- without it, every page in this loop got whichever
          // page happened to be active in the canvas at publish time's JS
          // pasted onto it instead of its own, exactly the bug #36 already
          // fixed for the navbar. This is why inline <script> blocks (e.g.
          // the contact-agent-form block, #37) were vanishing from published
          // pages even right after a fresh drag-and-republish.
          js: (mainComponent ? editor.getJs?.({ component: mainComponent }) : editor.getJs?.()) || '',
          // Flags which uploaded page's URL the backend should write back to
          // Deal.listing_url — lets it identify the deal's page without
          // guessing by slug string.
          isDealPage: activeDealPageId ? page.getId() === activeDealPageId : false,
        }
      })

      // Read the real subdomain record rather than re-deriving it from the
      // org name -- an org whose name collided with another's at signup gets
      // a randomly-suffixed real subdomain (createOrgSubdomain()), which a
      // fresh slugify of the org name can't reproduce. See subdomainFallback.js.
      const subdomain = deriveSubdomainSlug(user, currentSubdomain)

      if (!subdomain) { alert('Unable to determine subdomain. Please contact support.'); return }

      const envPrefix    = (import.meta as any).env?.VITE_DO_SPACES_FOLDER_PREFIX || 'production'
      const env          = envPrefix === 'production' ? '' : envPrefix
      const deployUrl    = `${subdomain}.${env}users.godreamx.com`

      if ((window as any).showNotification)
        (window as any).showNotification(`Deploying to ${deployUrl}...`, 'info')

      const API_BASE = (import.meta as any).env?.VITE_API_URL || 'http://localhost:4000'
      const token    = localStorage.getItem('jwt') || localStorage.getItem('token')
      const response = await fetch(`${API_BASE}/api/sites/deploy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify({ subdomain, pages }),
      })

      let data: any
      try {
        const ct = response.headers.get('content-type')
        if (ct?.includes('application/json')) data = await response.json()
        else { const t = await response.text(); try { data = JSON.parse(t) } catch { data = { error: t } } }
      } catch (e: any) { data = { error: e.message } }

      if (!response.ok) throw new Error(data?.error || data?.message || `HTTP ${response.status}`)

      if ((data.success || data.ok) && data.url) {
        const url = `${subdomain}.${env}users.godreamx.com`
        if ((window as any).showNotification) (window as any).showNotification(`Published to ${url}`, 'success')
        else alert(`Published!\n\nVisit: ${url}`)
        window.open(`https://${url}`, '_blank')
      } else if (data.message && (window as any).showNotification) {
        (window as any).showNotification(data.message, 'info')
      }
    } catch (err: any) {
      const msg = err?.message || 'Unknown error'
      if ((window as any).showNotification) (window as any).showNotification(`Failed to publish: ${msg}`, 'error', 6000)
      else alert(`Publish error: ${msg}`)
    }
  }, [user, currentSubdomain])

  useEffect(() => {
    (window as any).handlePublish = handlePublish
    return () => { delete (window as any).handlePublish }
  }, [handlePublish])

  const orgName  = user?.org
    ? (typeof user.org === 'string' ? user.org : (user.org as any).name)
    : 'Demo Realty'
  const initials = (user?.firstName?.[0] || '') + (user?.lastName?.[0] || '') || 'BW'
  const subdomain = deriveSubdomainSlug(user, currentSubdomain)
  const previewUrl = subdomain ? `https://${subdomain}.users.godreamx.com` : null

  return (
    <div className="dx-nav-root" style={{
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '0 28px',
      height: '56px',
      borderBottom: '1px solid #1f1f25',
      background: 'rgba(13,13,15,0.9)',
      backdropFilter: 'blur(14px)',
      flexShrink: 0,
      position: 'relative',
      zIndex: 10,
    }}>

      {/* Brand mark */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingRight: '18px' }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: `linear-gradient(135deg, ${accent}, color-mix(in oklab, ${accent} 40%, #000))`,
          display: 'grid', placeItems: 'center', color: 'white',
          boxShadow: '0 0 0 1px rgba(255,255,255,0.06) inset',
          cursor: 'pointer',
          flexShrink: 0,
        }} onClick={() => previewUrl && window.open(previewUrl, '_blank')} title="View live site">
          <GridIcon />
        </div>
        <div style={{ lineHeight: 1.2 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#ededf2', letterSpacing: '-0.01em', fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' }}>dreamx</div>
          <div className="dx-nav-subtitle" style={{ fontSize: 11, color: '#6b6b76', marginTop: 1, fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' }}>Web Builder</div>
        </div>
      </div>

      {/* Tab pill */}
      <div style={{
        display: 'flex', gap: 2, padding: 3,
        background: '#17171a', border: '1px solid #1f1f25',
        borderRadius: 10,
      }}>
        {[
          { label: 'Overview', Icon: GridIcon, onClick: onCloseEditor, active: true },
          { label: 'Editor',   Icon: PlayIcon, onClick: onOpenEditor,  active: false },
          { label: 'Settings', Icon: SettingsIcon, onClick: onOpenSettings, active: false },
        ].map(({ label, Icon, onClick, active }) => (
          <button
            key={label}
            className="dx-nav-tab-btn"
            onClick={onClick}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '6px 12px',
              fontSize: 12.5, fontWeight: 500,
              color: active ? '#ededf2' : '#a3a3ad',
              borderRadius: 7,
              background: active ? '#222228' : 'none',
              boxShadow: active ? '0 1px 0 rgba(255,255,255,0.04) inset' : 'none',
              transition: 'color 120ms, background 120ms',
              fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
              border: 0,
              cursor: 'pointer',
            }}
          >
            <Icon style={active ? { color: accent } : {}} />
            <span className="dx-nav-tab-label">{label}</span>
          </button>
        ))}
      </div>

      {/* Right actions */}
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Preview site */}
        <Tooltip title="Open your live site in a new tab" arrow placement="bottom">
          <button
            className="dx-nav-preview-btn"
            onClick={() => previewUrl && window.open(previewUrl, '_blank')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '7px 13px',
              fontSize: 13, fontWeight: 500,
              color: '#ededf2',
              background: '#17171a', border: '1px solid #1f1f25',
              borderRadius: 9, cursor: 'pointer',
              transition: 'background 140ms',
              fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = '#222228')}
            onMouseLeave={e => (e.currentTarget.style.background = '#17171a')}
          >
            <EyeIcon /> Preview site
          </button>
        </Tooltip>

        {/* Open editor CTA */}
        <Tooltip title="Open the drag-and-drop editor" arrow placement="bottom">
          <button
            onClick={onOpenEditor}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '7px 13px',
              fontSize: 13, fontWeight: 500,
              color: 'white',
              background: accent,
              border: `1px solid color-mix(in oklab, ${accent} 60%, #000)`,
              borderRadius: 9, cursor: 'pointer',
              boxShadow: '0 1px 0 rgba(255,255,255,0.18) inset, 0 4px 14px rgba(59,130,246,0.25)',
              transition: 'filter 140ms',
              fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
            }}
            onMouseEnter={e => (e.currentTarget.style.filter = 'brightness(1.08)')}
            onMouseLeave={e => (e.currentTarget.style.filter = 'none')}
          >
            <EditIcon /> <span className="dx-nav-open-editor-label">Open editor</span>
          </button>
        </Tooltip>

        {/* Divider */}
        <div style={{ width: 1, height: 24, background: '#28282f', margin: '0 4px' }} />

        {/* Org pill */}
        {user && (
          <div className="dx-nav-org-pill" style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '4px 8px 4px 4px',
            borderRadius: 9,
            background: '#17171a', border: '1px solid #1f1f25',
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: 6,
              background: `linear-gradient(135deg, ${accent}, ${accent}88)`,
              display: 'grid', placeItems: 'center',
              color: 'white', fontSize: 11, fontWeight: 700, flexShrink: 0,
              fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
            }}>
              {initials}
            </div>
            <div style={{ lineHeight: 1.2, maxWidth: 160, minWidth: 0 }}>
              <div className="dx-nav-org-name" style={{ fontSize: 12.5, fontWeight: 600, color: '#ededf2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' }}>
                {orgName}
              </div>
              <div className="dx-nav-org-email" style={{ fontSize: 10.5, color: '#6b6b76', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' }}>
                {user.email}
              </div>
            </div>
            <ChevDownIcon style={{ color: '#6b6b76', flexShrink: 0 }} />
          </div>
        )}

        {/* Sign out */}
        <Tooltip title="Sign out" arrow placement="bottom">
          <button
            onClick={logout}
            style={{
              width: 32, height: 32, borderRadius: 8,
              display: 'grid', placeItems: 'center',
              color: '#6b6b76', background: 'none', border: 0,
              cursor: 'pointer', transition: 'background 120ms, color 120ms',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#17171a'; e.currentTarget.style.color = '#ededf2' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none';    e.currentTarget.style.color = '#6b6b76' }}
            aria-label="Sign out"
          >
            <LogoutIcon />
          </button>
        </Tooltip>
      </div>
    </div>
  )
}
